import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
} from '../../utils/agGridFilters';

// deterministic hash → avatar palette (same approach as the other reskinned lists)
const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const OwnerCell = (params) => {
  const name = params.value;
  if (!name) return <span className="gos-cell-dash">—</span>;
  return (
    <div className="gos-owner-cell">
      <span className="gos-ava sm" style={{ background: avaColor(name) }}>
        {initials(name)}
      </span>
      <span className="gos-owner-name">{name}</span>
    </div>
  );
};

const ActiveCell = (params) => {
  const on = !!params.value;
  return (
    <span className={'gos-badge gos-b-' + (on ? 'green' : 'gray')}>
      <span className="gos-pip" />
      {on ? 'Yes' : 'No'}
    </span>
  );
};

// GrowthOS re-skin: no Actions column (matches Quotes + Sales). Code / Product
// Name are links to the Product view page; edit/delete are reached from there.
// Cells restyled: Code + Name links, Product Code mono, Owner avatar, Active badge.
const useProductColumnDefs = ({ gridData, owner }) => {
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const filterParams = useMemo(() => getSetFilterParams, []);

  const viewLink = (params, children, className) => (
    <Link
      to={`/products/productList/viewProduct?id=${params?.data?.id}`}
      state={{ ...params.data }}
      className={className}
    >
      {children}
    </Link>
  );

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Code',
        field: 'code',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) =>
          params.value
            ? viewLink(params, params.value, 'gos-lead-name')
            : <span className="gos-cell-dash">—</span>,
      },
      {
        headerName: 'Product Name',
        field: 'name',
        minWidth: 180,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) =>
          params.value ? viewLink(params, params.value, 'gos-lead-link') : <span className="gos-cell-dash">—</span>,
      },
      {
        headerName: 'Product Code',
        field: 'productCode',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        cellRenderer: (params) =>
          params.value ? <span className="gos-mono-cell">{params.value}</span> : <span className="gos-cell-dash">—</span>,
      },
      {
        headerName: 'Product Owner',
        field: 'productOwner',
        minWidth: 190,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
        cellRenderer: OwnerCell,
      },
      {
        headerName: 'Active',
        field: 'productActive',
        width: 120,
        cellRenderer: ActiveCell,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
          buttons: ['apply', 'reset'],
        },
      },
    ];
  }, [commonTextFilterParams, filterParams, gridData, owner]);

  return columnDefs;
};

export default useProductColumnDefs;
